import { isUsableMemberId } from './member-id'
import type { Member } from '../store/types'

/**
 * Who the member picker may offer, and what to call them.
 *
 * The picker cannot simply render the store's `members`, for three reasons that
 * all arrive on the same screen:
 *
 *   - a fresh install has no members at all until the first pull, and one with
 *     no backend configured never will — but the person holding it still has a
 *     list of chores and still ticks boxes as somebody;
 *   - a member row whose id contains a colon would crash the day list, because
 *     `encodeCompletionId` throws on one (`src/lib/member-id.ts`);
 *   - the signed-in member must always be pickable, even if their own row has
 *     not come down yet, or the app would render a picker with no way to get
 *     back to your own chores.
 *
 * So the fallback is synthesised here, in the view layer, and never written to
 * the store. That distinction matters more than it looks: a synthesised row in
 * the store would be marked dirty and pushed into a household that already has
 * its own people, which is the class of problem `../semainier` needs `seedMeta`
 * and `adoptAccount` to solve and this app has so far avoided entirely.
 */

/** Just enough of a member to draw a chip and fill a select. */
export interface MemberOption {
  id: string
  displayName: string
}

/** What the signed-in member is called before their row has arrived. */
export const FALLBACK_MEMBER_NAME = 'Moi'

export function visibleMembers(
  members: readonly Member[],
  selfMemberId: string,
): MemberOption[] {
  const options: MemberOption[] = []

  for (const member of members) {
    if (!isUsableMemberId(member.id)) continue
    options.push({ id: member.id, displayName: member.displayName })
  }

  // Ahead of the alphabetical rest, because it is the list you look at every
  // day and the one the app opens on.
  const self = options.findIndex((option) => option.id === selfMemberId)
  if (self >= 0) {
    const [mine] = options.splice(self, 1)
    return [mine!, ...options]
  }

  if (!isUsableMemberId(selfMemberId)) return options
  return [{ id: selfMemberId, displayName: FALLBACK_MEMBER_NAME }, ...options]
}

/** A member's name for display, or a placeholder — never an empty chip. */
export function memberName(
  members: readonly MemberOption[],
  memberId: string,
  fallback = 'Quelqu’un',
): string {
  return members.find((member) => member.id === memberId)?.displayName || fallback
}
